import { useEffect, useState } from 'react';
import type { FamilyDevice, FindNowStage, LocationPoint } from './types';
import { accuracy, fromViewer, landmark, primary, secondary } from './locationDisplay';
import { devicePresence, locationFreshness } from './status';
import { PrimaryButton, SecondaryButton, Sheet, TechnicalDetails } from './FigmaComponents';
import { findNowFailureCopy, findNowWaitingCopy, isTerminalFindNowStage } from './findNow';

const steps: Array<{ stage: FindNowStage; label: string }> = [
  { stage: 'REQUEST_CREATED', label: 'Request created' }, { stage: 'CONTACTING_DEVICE', label: 'Contacting device' },
  { stage: 'DEVICE_REACHED', label: 'Device reached' }, { stage: 'ACQUIRING_LOCATION', label: 'Acquiring location' },
  { stage: 'RESOLVING_ADDRESS', label: 'Resolving address' }
];
const rank: Record<string, number> = {
  REQUEST_CREATED: 0, PUSH_SUBMITTED: 1, CONTACTING_DEVICE: 1, DEVICE_RECEIVED: 2, DEVICE_REACHED: 2,
  EXECUTION_STARTED: 3, LOCATION_ACQUIRING: 3, ACQUIRING_LOCATION: 3, LOCATION_ACQUIRED: 3,
  RESULT_UPLOADING: 4, RESOLVING_ADDRESS: 4, SUCCESS: 5
};

/**
 * How long this request has been waiting, ticking once a second.
 *
 * Its own clock rather than the dashboard's 10s tick, which is too coarse to
 * land the 15s and 30s wording anywhere near where it belongs. It runs from
 * startedAt, so it measures the request and not the wording: changing what is
 * displayed can never restart it. It stops the moment the stage is terminal.
 */
function useElapsedSince(startedAt: number, active: boolean) {
  const [elapsedMs, setElapsedMs] = useState(() => Math.max(0, Date.now() - startedAt));
  useEffect(() => {
    setElapsedMs(Math.max(0, Date.now() - startedAt));
    if (!active) return;
    const timer = window.setInterval(() => setElapsedMs(Math.max(0, Date.now() - startedAt)), 1_000);
    return () => window.clearInterval(timer);
  }, [startedAt, active]);
  return elapsedMs;
}

export function FindNowSheet({ device, stage, startedAt, point, viewerPoint, error, onCancel, onRetry, onClose, onPing, onHistory, onMap }: {
  device: FamilyDevice; stage: FindNowStage; startedAt: number; point?: LocationPoint | null; viewerPoint?: LocationPoint | null; error?: string;
  onCancel: () => void; onRetry: () => void; onClose: () => void; onPing: () => void; onHistory: () => void; onMap: () => void;
}) {
  const elapsedMs = useElapsedSince(startedAt, !isTerminalFindNowStage(stage));
  const current = point || device.location;
  if (stage === 'SUCCESS' && current) {
    const fresh = locationFreshness(current);
    return <Sheet className="find-sheet success-sheet" aria-live="polite">
      <small className="sheet-eyebrow">FIND NOW RESULT</small><h2>{device.owner} located</h2>
      <span className={`fig-status ${fresh.tone}`}><span className="fig-status-dot" />{fresh.label} · {fresh.age === '0s ago' ? 'just now' : fresh.age}</span>
      <div className="result-place"><strong>{primary(current)}</strong>{secondary(current) && <span>{secondary(current)}</span>}{landmark(current) && <span>{landmark(current)}</span>}</div>
      <div className="result-facts"><b>{accuracy(current)}</b><b>{fromViewer(current, viewerPoint) || 'Distance unavailable'}</b></div>
      <div className="result-presence"><span className={devicePresence(device.lastSeenAt, Date.now(), device.reachability==='ONLINE', device.lastContactVia).online ? 'online-dot' : 'offline-dot'} />Device {devicePresence(device.lastSeenAt, Date.now(), device.reachability==='ONLINE', device.lastContactVia).label.toLowerCase()} · last signal {devicePresence(device.lastSeenAt).age}</div>
      <PrimaryButton onClick={onMap}>View on Map</PrimaryButton>
      <div className="two-actions"><SecondaryButton onClick={onPing}>Ping Device</SecondaryButton><SecondaryButton onClick={onHistory}>View History</SecondaryButton></div>
      <TechnicalDetails point={current} /><small className="sheet-foot">Shared by {device.owner} · technical coordinates hidden by default</small>
      <button className="sheet-close" aria-label="Close Find Now result" onClick={onClose}>×</button>
    </Sheet>;
  }

  if (['DELIVERY_TIMEOUT','LOCATION_PERMISSION_REQUIRED','LOCATION_SERVICES_DISABLED','LOCATION_UNAVAILABLE','LOCATION_TIMEOUT','NETWORK_UPLOAD_FAILED','COMMAND_EXPIRED','DEVICE_OFFLINE','PERMISSION_UNAVAILABLE','TIMEOUT'].includes(stage)) {
    const copy = findNowFailureCopy(stage, device.owner, error);
    return <Sheet className="find-sheet recovery-sheet" aria-live="polite">
      <span className="exception-pill">{copy.label}</span><h2>{copy.title}</h2>
      <p>{copy.body}</p>
      {device.location && <div className="last-confirmed"><small>LAST CONFIRMED</small><strong>{primary(device.location)}</strong><span>{secondary(device.location)}</span><span>{locationFreshness(device.location).age}</span></div>}
      <PrimaryButton onClick={onRetry}>Try Again</PrimaryButton><SecondaryButton onClick={onMap}>Show Last Location</SecondaryButton>
      <button className="sheet-close" aria-label="Close" onClick={onClose}>×</button>
    </Sheet>;
  }

  const idx = rank[stage] ?? 0;
  // Wording only. The step list below still reports the authoritative stage,
  // and neither this nor the elapsed clock can change what that stage is.
  const { title, body } = findNowWaitingCopy(stage, elapsedMs);
  return <Sheet className="find-sheet progress-sheet" aria-live="polite">
    <small className="sheet-eyebrow">FIND NOW</small><h2>{title}</h2><p>{body}</p>
    <div className="find-progress">{steps.map((item, i) => <div key={item.stage} className={`progress-step ${i < idx ? 'done' : i === idx ? 'active' : ''}`}><i /> <span>{item.label}</span></div>)}</div>
    <small className="security-note">Request is consent-bound · {device.owner} can see who requested it</small>
    <SecondaryButton onClick={onCancel}>Dismiss</SecondaryButton>
  </Sheet>;
}
